import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SpinnerGap, FloppyDisk } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SplitConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/split-config`, { withCredentials: true });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/split-config`, config, { withCredentials: true });
      alert('Configuration saved successfully!');
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerGap size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
          Global Split Configuration
        </h1>
        <p className="text-slate-500 mt-1">Configure default percentage distribution of incentives across roles. Changes apply to new placements only.</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Percentage Distribution</CardTitle>
          <CardDescription>
            These defaults apply when calculating incentive splits. Actual amounts are determined by the Incentive Matrix.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Recruiter Percentage (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={config?.recruiter_percent || 0}
                onChange={(e) => setConfig({ ...config, recruiter_percent: parseFloat(e.target.value) || 0 })}
                data-testid="input-recruiter-percent"
              />
              <p className="text-xs text-slate-500">Percentage of total incentive going to the recruiter</p>
            </div>

            <div className="space-y-2">
              <Label>Sourcing Percentage (% of Recruiter Split)</Label>
              <Input
                type="number"
                step="0.1"
                value={config?.sourcing_percent_of_recruiter || 0}
                onChange={(e) => setConfig({ ...config, sourcing_percent_of_recruiter: parseFloat(e.target.value) || 0 })}
                data-testid="input-sourcing-percent"
              />
              <p className="text-xs text-slate-500">Percentage of recruiter's split going to sourcing support</p>
            </div>

            <div className="space-y-2">
              <Label>Lead Percentage (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={config?.lead_percent || 0}
                onChange={(e) => setConfig({ ...config, lead_percent: parseFloat(e.target.value) || 0 })}
                data-testid="input-lead-percent"
              />
              <p className="text-xs text-slate-500">Percentage of total incentive going to leads</p>
            </div>

            <div className="space-y-2">
              <Label>Manager Percentage (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={config?.manager_percent || 0}
                onChange={(e) => setConfig({ ...config, manager_percent: parseFloat(e.target.value) || 0 })}
                data-testid="input-manager-percent"
              />
              <p className="text-xs text-slate-500">Percentage of total incentive going to managers</p>
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-3">Split Preview (on ₹100,000 placement)</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Recruiter ({config?.recruiter_percent}%)</span>
                <span className="font-medium">₹{((config?.recruiter_percent / 100) * 100000).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between pl-4">
                <span className="text-slate-400">↳ Sourcing ({config?.sourcing_percent_of_recruiter}% of recruiter)</span>
                <span className="font-medium text-slate-600">
                  ₹{(((config?.recruiter_percent / 100) * 100000) * (config?.sourcing_percent_of_recruiter / 100)).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Lead ({config?.lead_percent}%)</span>
                <span className="font-medium">₹{((config?.lead_percent / 100) * 100000).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Manager ({config?.manager_percent}%)</span>
                <span className="font-medium">₹{((config?.manager_percent / 100) * 100000).toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving} data-testid="save-config-btn">
            {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
            Save Configuration
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SplitConfigPage;
